import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Clock, 
  ChevronRight,
  Share2
} from 'lucide-react';
import SEO from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { BackgroundAnimation } from '@/components/background-animation';
import { BLOG_POSTS } from '@/data/blog-posts';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { BlogPost } from '@/types/blog';

const categories = ['All', 'Security', 'Business', 'Tutorial', 'Physics'];

const BlogIndex: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const filteredPosts = BLOG_POSTS.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white text-foreground selection:bg-primary selection:text-primary-foreground font-sans pb-24 overflow-x-hidden relative">
      <div className="absolute inset-x-0 top-0 h-[400px] bg-[#f8fbff] -z-20" />
      <div className="absolute inset-0 noise-bg opacity-[0.01] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(6,182,212,0.06)_0%,transparent_70%)]" />
        <div className="absolute bottom-0 left-0 w-full h-[500px] bg-[radial-gradient(circle_at_20%_100%,rgba(6,182,212,0.03)_0%,transparent_60%)]" />
      </div>
      <SEO 
        title="Blog"
        description="Exploring the intersection of physics, psychology, and digital craftsmanship. The Fun Circle Blog."
        keywords="physics blog, satisfying physics, ASMR simulation, creator economy"
        canonical="/blog"
      />
      <div className="max-w-6xl mx-auto px-6 pt-24 relative z-10">
        <div className="flex flex-col items-start gap-4 mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-[9px] font-black text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="relative">
            <h1 className="text-3xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase leading-[0.9]">
              The <span className="text-cyan-600/40">Journal</span>
            </h1>
          </div>
        </div>

        {/* Resource Navigation */}
        <div className="flex flex-wrap gap-2 items-center mb-8 text-[9px] font-black uppercase tracking-widest text-zinc-400">
          <span className="mr-2">Learning Hubs:</span>
          <Link to="/tutorials" className="px-3 py-1 bg-zinc-50 border border-zinc-100 hover:border-cyan-600 hover:text-cyan-600 rounded-full transition-colors">Tutorials</Link>
          <Link to="/case-studies" className="px-3 py-1 bg-zinc-50 border border-zinc-100 hover:border-cyan-600 hover:text-cyan-600 rounded-full transition-colors">Case Studies</Link>
          <Link to="/showcase" className="px-3 py-1 bg-zinc-50 border border-zinc-100 hover:border-cyan-600 hover:text-cyan-600 rounded-full transition-colors">Showcase</Link>
          <Link to="/faq" className="px-3 py-1 bg-zinc-50 border border-zinc-100 hover:border-cyan-600 hover:text-cyan-600 rounded-full transition-colors">FAQ</Link>
          <Link to="/help" className="px-3 py-1 bg-zinc-50 border border-zinc-100 hover:border-cyan-600 hover:text-cyan-600 rounded-full transition-colors">Help Center</Link>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12 py-4 border-y border-cyan-600/10">
          <div className="flex flex-wrap items-center gap-6">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-[10px] font-bold uppercase tracking-[0.2em] transition-all relative py-2 ${
                  activeCategory === cat 
                    ? 'text-cyan-600' 
                    : 'text-zinc-400 hover:text-zinc-600'
                }`}
              >
                {cat}
                {activeCategory === cat && (
                  <motion.div layoutId="catUnderline" className="absolute bottom-0 left-0 w-full h-[2px] bg-cyan-600" />
                )}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
            <input 
              placeholder="Search Articles..." 
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent border-0 border-b border-cyan-600/10 pl-6 pb-1 text-[10px] font-bold uppercase tracking-widest focus:outline-none focus:border-cyan-600 transition-colors placeholder:text-zinc-300"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-12">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-zinc-50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-12">
            <AnimatePresence mode="popLayout">
              {filteredPosts.map((post, index) => (
                <motion.div
                  key={post.slug}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    delay: index * 0.03,
                    duration: 0.5
                  }}
                  className="group relative"
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-8 pb-12 border-b border-cyan-600/5">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                        <span className="text-cyan-600">{post.category}</span>
                        <span className="w-1 h-1 rounded-full bg-zinc-200" />
                        <span>{post.readTime}</span>
                      </div>
                      
                      <Link to={`/blog/${post.slug}`} className="block">
                        <h3 className="text-2xl md:text-3xl font-bold text-cyan-950 leading-tight tracking-tight hover:text-cyan-600 transition-colors duration-300">
                          {post.title}
                        </h3>
                      </Link>

                      <p className="text-zinc-500 text-sm leading-relaxed line-clamp-2 max-w-3xl">
                        {post.description}
                      </p>

                      <div className="flex items-center gap-6 pt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-cyan-950 uppercase">{post.author.name}</span>
                          <span className="text-[10px] text-cyan-200 font-medium whitespace-pre">•</span>
                          <span className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider">{post.date}</span>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.origin + '/blog/' + post.slug)}`, '_blank');
                            }}
                            className="text-cyan-200 hover:text-cyan-600 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              const url = window.location.origin + '/blog/' + post.slug;
                              if (navigator.share) {
                                navigator.share({ title: post.title, url });
                              } else {
                                navigator.clipboard.writeText(url);
                                alert('Link copied to clipboard!');
                              }
                            }}
                            className="text-cyan-200 hover:text-cyan-600 transition-colors"
                          >
                            <Share2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <Link to={`/blog/${post.slug}`} className="hidden md:flex items-center justify-center w-12 h-12 rounded-full border border-cyan-100 hover:bg-cyan-600 hover:text-white transition-all group/btn text-cyan-600 shadow-sm">
                      <ChevronRight className="w-5 h-5 group-hover/btn:translate-x-0.5 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        <div className="mt-24">
          <DiscordCTA variant="banner" />
        </div>

        <div className="mt-12">
          <InternalLinksSection />
        </div>
      </div>
    </div>
  );
};

export default BlogIndex;
